import { useState } from 'react';
import { useCarrinhoCompras } from '../../contexts/CarrinhoComprasContext';
import { salvarPedidoHistorico } from '../../services/api';
import styles from './ListaComprasPanel.module.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// gerarPDF é chamado apenas de dentro da página de Histórico (após aprovação)
export { gerarPDF };

// Comprime uma imagem (blob) para um JPEG pequeno via canvas.
// PDF mostra cada foto em ~22mm — não vale carregar 3MB pra isso.
const comprimirImagemParaPDF = (blob, maxLado = 220, quality = 0.72) => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const ratio = Math.min(maxLado / img.naturalWidth, maxLado / img.naturalHeight, 1);
        const w = Math.max(1, Math.round(img.naturalWidth * ratio));
        const h = Math.max(1, Math.round(img.naturalHeight * ratio));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } catch {
        resolve(null);
      } finally {
        URL.revokeObjectURL(url);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
};

const gerarPDF = async (itens) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // ── Carregar imagens dos produtos ──────────────────────────────────────────
  const imagensMap = {};
  const carregarImagem = async (url) => {
    if (!url || url === '' || url === 'null') return null;

    // URL relativa interna → fetch direto; URL externa → proxy do backend
    let fetchUrl;
    if (url.startsWith('/')) {
      fetchUrl = url;
    } else if (url.startsWith('http')) {
      fetchUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
    } else {
      return null;
    }

    try {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 15000);
      const resp = await fetch(fetchUrl, { signal: controller.signal });
      clearTimeout(tid);
      if (!resp.ok || resp.status === 204) return null;
      const blob = await resp.blob();
      if (!blob || blob.size < 100) return null;
      // Comprime antes de retornar — reduz 3MB → ~15-25KB sem afetar visual no PDF
      return await comprimirImagemParaPDF(blob, 220, 0.72);
    } catch {
      return null;
    }
  };

  // Carregar todas as imagens em batches pequenos (paralelo controlado)
  for (let i = 0; i < itens.length; i += 5) {
    const batch = itens.slice(i, i + 5);
    const promises = batch.map(async ({ produto }) => {
      const codigo = produto.codigo || produto.codigoInterno || produto.codigoBarras || '';
      if (!codigo) return;

      // 1ª: cache local; 2ª: URL externa do produto via proxy
      let imgData = await carregarImagem(`/api/produtos/imagem-pdf/${encodeURIComponent(codigo)}`);
      if (!imgData) {
        const urlExterna = produto.imagemURL || produto.imagemBase64 || produto.imagem_url || produto.imagemUrl;
        if (urlExterna && typeof urlExterna === 'string' && urlExterna.startsWith('http')) {
          imgData = await carregarImagem(urlExterna);
        }
      }
      if (imgData) imagensMap[codigo] = imgData;
    });
    await Promise.all(promises);
  }

  // ── Cabeçalho ──────────────────────────────────────────────────────────────
  const dataHoje = new Date().toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });

  doc.setFillColor(22, 163, 74);
  doc.rect(0, 0, 210, 30, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('LISTA DE COMPRAS — E-COMMERCE', 14, 13);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Gerado em: ${dataHoje}`, 14, 22);
  doc.text(`Total de produtos: ${itens.length}`, 100, 22);

  // ── Agrupar por fornecedor e ordenar ──────────────────────────────────────
  const grupos = {};
  itens.forEach(({ produto, quantidade }) => {
    const forn = produto.fornecedor || 'Sem Fornecedor';
    if (!grupos[forn]) grupos[forn] = [];
    grupos[forn].push({ produto, quantidade });
  });

  const fornecedoresOrdenados = Object.keys(grupos).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  let posY = 38;

  const formatarMoeda = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  let totalGeralQtd = 0;
  let totalGeralValor = 0;

  fornecedoresOrdenados.forEach((fornecedor) => {
    const itensForn = grupos[fornecedor];
    itensForn.sort((a, b) => (b.produto.precoCusto || 0) - (a.produto.precoCusto || 0));

    const subtotalQtd = itensForn.reduce((acc, i) => acc + i.quantidade, 0);
    const subtotalValor = itensForn.reduce(
      (acc, i) => acc + (i.produto.precoCusto || 0) * i.quantidade * 12, 0
    );

    // Guardar códigos para inserir imagens via didDrawCell
    const codigosDestaTabela = [];

    const body = itensForn.map(({ produto, quantidade }) => {
      const codigo = produto.codigo || produto.codigoInterno || '';
      const nome = produto.nome || produto.descricao || '';
      const precoCusto = produto.precoCusto || 0;
      const unidades = quantidade * 12;
      const subtotal = precoCusto * unidades;
      totalGeralQtd += quantidade;
      totalGeralValor += subtotal;
      codigosDestaTabela.push(codigo);

      return [
        '',
        `${codigo}\n${nome}`,
        String(quantidade),
        precoCusto > 0 ? formatarMoeda(precoCusto) : '-',
        precoCusto > 0 ? formatarMoeda(subtotal) : '-',
      ];
    });

    const tableResult = autoTable(doc, {
      startY: posY,
      // Header do fornecedor + colunas repete em cada página da MESMA tabela
      showHead: 'everyPage',
      head: [
        [
          { content: fornecedor, colSpan: 5, styles: { fillColor: [30, 39, 54], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, cellPadding: { top: 5, bottom: 5, left: 8, right: 8 } } }
        ],
        [
          { content: 'Foto',               styles: { fillColor: [51, 65, 85], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7, halign: 'center',  cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } } },
          { content: 'Código / Descrição', styles: { fillColor: [51, 65, 85], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7,                    cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } } },
          { content: 'Dz.',                styles: { fillColor: [51, 65, 85], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7, halign: 'center',  cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } } },
          { content: 'Vlr. Unit.',         styles: { fillColor: [51, 65, 85], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7, halign: 'right',   cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } } },
          { content: 'Subtotal',           styles: { fillColor: [51, 65, 85], textColor: [255,255,255], fontStyle: 'bold', fontSize: 7, halign: 'right',   cellPadding: { top: 1, bottom: 1, left: 2, right: 2 } } },
        ]
      ],
      body,
      foot: [[
        { content: 'SUBTOTAL', colSpan: 2, styles: { fontStyle: 'bold', fillColor: [240, 253, 244], textColor: [22, 163, 74] } },
        { content: `${subtotalQtd} dz`, styles: { fontStyle: 'bold', fillColor: [240, 253, 244], textColor: [22, 163, 74], halign: 'center' } },
        { content: '', styles: { fillColor: [240, 253, 244] } },
        { content: formatarMoeda(subtotalValor), styles: { fontStyle: 'bold', fillColor: [240, 253, 244], textColor: [22, 163, 74], halign: 'right' } },
      ]],
      // Sem 'columns' — usando arrays diretos no body
      columnStyles: {
        0: { cellWidth: 26, halign: 'center', valign: 'middle' },
        1: { cellWidth: 'auto', fontSize: 8 },
        2: { cellWidth: 14, halign: 'center', fontSize: 9, fontStyle: 'bold', valign: 'middle' },
        3: { cellWidth: 28, halign: 'right', fontSize: 8, valign: 'middle' },
        4: { cellWidth: 30, halign: 'right', fontSize: 8, fontStyle: 'bold', valign: 'middle' },
      },
      styles: { overflow: 'linebreak', cellPadding: 3, minCellHeight: 26 },
      headStyles: { minCellHeight: 0 },
      alternateRowStyles: { fillColor: [247, 250, 252] },
      theme: 'grid',
      didDrawCell(data) {
        if (data.section === 'body' && data.column.index === 0) {
          const codigo = codigosDestaTabela[data.row.index];
          const imgData = imagensMap[codigo];
          if (imgData) {
            const imgSize = 22;
            const x = data.cell.x + (data.cell.width - imgSize) / 2;
            const y = data.cell.y + (data.cell.height - imgSize) / 2;
            try { doc.addImage(imgData, 'JPEG', x, y, imgSize, imgSize); } catch {}
          }
        }
      },
    });

    posY = (tableResult?.finalY ?? doc.lastAutoTable?.finalY ?? posY) + 8;

    if (posY > 260 && fornecedoresOrdenados.indexOf(fornecedor) < fornecedoresOrdenados.length - 1) {
      doc.addPage();
      posY = 20;
    }
  });

  // ── Rodapé geral ────────────────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(
      `Página ${i} de ${pageCount} — Dashboard Gringão`,
      doc.internal.pageSize.getWidth() / 2,
      290,
      { align: 'center' }
    );
  }

  doc.setPage(pageCount);
  const finalY = Math.min((doc.lastAutoTable?.finalY ?? posY) + 12, 275);
  doc.setFillColor(22, 163, 74);
  doc.roundedRect(14, finalY, 182, 14, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`TOTAL GERAL: ${totalGeralQtd} dz — ${totalGeralQtd * 12} un.`, 20, finalY + 9);
  doc.text(
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalGeralValor),
    196 - 6,
    finalY + 9,
    { align: 'right' }
  );

  const nomeArquivo = `lista_compras_ecommerce_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(nomeArquivo);
};

// ─────────────────────────────────────────────────────────────────────────────

const ListaComprasPanel = ({ onClose }) => {
  const { itens, removerItem, atualizarQuantidade, limparCarrinho } = useCarrinhoCompras();
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const formatarMoeda = (v) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const totalGeral = itens.reduce(
    (acc, { produto, quantidade }) => acc + (produto.precoCusto || 0) * quantidade * 12, 0
  );

  const handleEnviarAprovacao = async () => {
    if (itens.length === 0) return;
    setEnviando(true);
    try {
      const resultado = await salvarPedidoHistorico({
        titulo: `Pedido ${new Date().toLocaleDateString('pt-BR')}`,
        itens,
      });
      if (resultado.success) {
        setEnviado(true);
        setTimeout(() => {
          limparCarrinho();
          onClose();
        }, 2000);
      }
    } finally {
      setEnviando(false);
    }
  };

  // Agrupar por fornecedor para exibição no painel
  const grupos = {};
  itens.forEach(item => {
    const forn = item.produto.fornecedor || 'Sem Fornecedor';
    if (!grupos[forn]) grupos[forn] = [];
    grupos[forn].push(item);
  });
  const fornecedores = Object.keys(grupos).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          <span className={styles.panelIcon}>🛒</span>
          <span>Lista de Compras</span>
          <span className={styles.badge}>{itens.length}</span>
        </div>
        <button className={styles.closeBtn} onClick={onClose} title="Fechar">✕</button>
      </div>

      <div className={styles.panelBody}>
        {itens.length === 0 ? (
          <div className={styles.vazio}>
            <div className={styles.vazioIcone}>📋</div>
            <div className={styles.vazioTexto}>Nenhum produto adicionado</div>
            <div className={styles.vazioSub}>
              Ative o modo de seleção e clique nos produtos para adicioná-los
            </div>
          </div>
        ) : (
          <>
            {fornecedores.map(forn => (
              <div key={forn} className={styles.grupo}>
                <div className={styles.grupoHeader}>
                  <span className={styles.grupoIcone}>🏭</span>
                  <span className={styles.grupoNome}>{forn}</span>
                  <span className={styles.grupoCount}>{grupos[forn].length}</span>
                </div>
                <div className={styles.grupoItens}>
                  {grupos[forn].map(({ produto, quantidade }) => {
                    const codigo = produto.codigo || produto.codigoInterno;
                    const nome = produto.nome || produto.descricao;
                    const precoCusto = produto.precoCusto || 0;
                    return (
                      <div key={codigo} className={styles.item}>
                        <div className={styles.itemInfo}>
                          <div className={styles.itemNome} title={nome}>{nome}</div>
                          <div className={styles.itemCodigo}>#{codigo}</div>
                          {precoCusto > 0 && (
                            <div className={styles.itemPreco}>
                              {formatarMoeda(precoCusto)} / un.
                              <span style={{ color: '#64748b', fontSize: '0.7rem' }}> × {quantidade * 12} un.</span>
                              {quantidade > 0 && (
                                <span className={styles.itemSubtotal}>
                                  = {formatarMoeda(precoCusto * quantidade * 12)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className={styles.itemControls}>
                          <div className={styles.qtdRow}>
                            <button
                              className={styles.qtdMini}
                              onClick={() => atualizarQuantidade(codigo, quantidade - 1)}
                            >−</button>
                            <input
                              type="number"
                              className={styles.qtdInputMini}
                              value={quantidade}
                              min={1}
                              onChange={e => atualizarQuantidade(codigo, e.target.value)}
                            />
                            <button
                              className={styles.qtdMini}
                              onClick={() => atualizarQuantidade(codigo, quantidade + 1)}
                            >+</button>
                          </div>
                          <div style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center' }}>
                            {quantidade * 12} un.
                          </div>
                          <button
                            className={styles.removerBtn}
                            onClick={() => removerItem(codigo)}
                            title="Remover produto"
                          >🗑️</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {itens.length > 0 && (
        <div className={styles.panelFooter}>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total estimado (custo):</span>
            <span className={styles.totalValor}>{formatarMoeda(totalGeral)}</span>
          </div>
          <div className={styles.footerBtns}>
            <button
              className={styles.btnLimpar}
              onClick={() => { if (window.confirm('Limpar toda a lista de compras?')) limparCarrinho(); }}
              disabled={enviando || enviado}
            >
              🗑️ Limpar
            </button>
            <button
              className={styles.btnPDF}
              onClick={handleEnviarAprovacao}
              disabled={enviando || enviado}
            >
              {enviado
                ? <><span>✅</span> Enviado!</>
                : enviando
                ? <><span className={styles.spinner}></span> Enviando...</>
                : <><span>📤</span> Enviar para Aprovação</>
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaComprasPanel;
